// report.ts — flexible block-based report model.
//
// An analysis report has 4 parts (analyse | vorschlag | roi | finanzierung).
// Each part is a list of Sections; each Section has a title and an ordered list
// of Blocks. A Block is a discriminated union on `type` — <BlockRenderer> switches
// exhaustively over it. This replaces the fixed BESS-style schema in analysis.ts
// for screen rendering (the PDF page still uses the old schema for now).

export type BlockType =
  | 'heading'
  | 'paragraph'
  | 'kpi_grid'
  | 'table'
  | 'bar_chart'
  | 'line_chart'
  | 'heatmap'
  | 'callout'

export interface KpiItem {
  value: string | number
  label: string
  unit?: string
}

export interface ChartPoint {
  x: string | number
  y: number
}

export interface ChartSeries {
  name: string
  points: ChartPoint[]
}

export type CalloutVariant = 'info' | 'warning' | 'success'

export interface HeadingBlock {
  type: 'heading'
  text: string
}

export interface ParagraphBlock {
  type: 'paragraph'
  text: string // markdown
}

export interface KpiGridBlock {
  type: 'kpi_grid'
  items: KpiItem[]
}

export interface TableBlock {
  type: 'table'
  title?: string
  columns: string[]
  rows: (string | number)[][]
}

export interface BarChartBlock {
  type: 'bar_chart'
  title?: string
  x_label?: string
  y_label?: string
  series: ChartSeries[]
}

export interface LineChartBlock {
  type: 'line_chart'
  title?: string
  x_label?: string
  y_label?: string
  series: ChartSeries[]
}

export interface HeatmapBlock {
  type: 'heatmap'
  title?: string
  x_labels: string[]
  y_labels: string[]
  values: number[][]
  unit?: string
}

export interface CalloutBlock {
  type: 'callout'
  title?: string
  text: string
  variant?: CalloutVariant
  priority?: string
}

export type Block =
  | HeadingBlock
  | ParagraphBlock
  | KpiGridBlock
  | TableBlock
  | BarChartBlock
  | LineChartBlock
  | HeatmapBlock
  | CalloutBlock

export interface Section {
  section_title: string
  blocks: Block[]
}

export type ReportPart = 'analyse' | 'vorschlag' | 'roi' | 'finanzierung'

export interface ReportCover {
  subtitle?: string
  period?: string
}

export interface AnalysisReport {
  cover?: ReportCover
  analyse: Section[]
  vorschlag: Section[]
  roi: Section[]
  finanzierung: Section[]
}

export const REPORT_PART_LABELS: Record<ReportPart, string> = {
  analyse: 'Analyse',
  vorschlag: 'Vorschlag',
  roi: 'ROI',
  finanzierung: 'Finanzierung',
}

export const REPORT_PART_ORDER: ReportPart[] = ['analyse', 'vorschlag', 'roi', 'finanzierung']
